import axios, { AxiosError } from "axios";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";

import {
  InboxQueryApiRequest,
  InboxQueryDetails,
  InboxQueryResolveApiRequest,
  InboxQueryListApiRequest,
  InboxQueryListApiResponse,
  InboxQueryResolveApiResponse,
  InboxQueryStatus,
  InboxTabs,
  FilterOptionsResponse,
  InboxFilterState,
} from "../../utils/types";

/**
 * Determines which categories should be sent based on enabled flags
 * Categories are included if they are enabled, regardless of specific filters
 */
const determineCategories = (
  queriesEnabled: boolean,
  leaveRequestsEnabled: boolean
): string => {
  const categories: string[] = [];

  if (queriesEnabled) categories.push("QUERY");
  if (leaveRequestsEnabled) categories.push("LEAVE");

  return categories.join(",");
};

/**
 * Adds filter parameters for enabled categories with selections
 */
const addFilterParameters = (
  appliedFilters: InboxFilterState,
  hasQueryFilters: boolean
): Record<string, string> => {
  const params: Record<string, string> = {};

  if (appliedFilters.queriesEnabled && hasQueryFilters) {
    params.query_types = appliedFilters.selectedQueryTypes.join(",");
  }

  if (
    appliedFilters.leaveRequestsEnabled &&
    appliedFilters.selectedLeaveTypes.length > 0
  ) {
    params.leave_types = appliedFilters.selectedLeaveTypes.join(",");
  }

  if (
    appliedFilters.leaveRequestsEnabled &&
    appliedFilters.selectedLeaveStatuses.length > 0
  ) {
    params.leave_statuses = appliedFilters.selectedLeaveStatuses.join(",");
  }

  return params;
};

/**
 * Interface for validated filter parameters sent to the API
 */
interface ValidatedFilterParams {
  categories?: string;
  query_types?: string;
  leave_types?: string;
  leave_statuses?: string;
}

/**
 * Validates and cleans filter parameters to prevent API validation errors
 */
const validateAndCleanFilters = (
  appliedFilters: InboxFilterState
): ValidatedFilterParams => {
  const filterParams: ValidatedFilterParams = {};

  const hasQueryFilters = appliedFilters.selectedQueryTypes.length > 0;

  // Determine and set categories
  const categoriesString = determineCategories(
    appliedFilters.queriesEnabled,
    appliedFilters.leaveRequestsEnabled
  );

  if (categoriesString) {
    filterParams.categories = categoriesString;
  }

  // Add specific filter parameters explicitly to avoid conflicts
  const additionalParams = addFilterParameters(appliedFilters, hasQueryFilters);

  // Explicitly assign each property to maintain control over precedence
  if (additionalParams.query_types) {
    filterParams.query_types = additionalParams.query_types;
  }
  if (additionalParams.leave_types) {
    filterParams.leave_types = additionalParams.leave_types;
  }
  if (additionalParams.leave_statuses) {
    filterParams.leave_statuses = additionalParams.leave_statuses;
  }

  return filterParams;
};

/**
 * Builds URL and params for ticket list requests
 */
const buildListRequestConfig = (
  request: InboxQueryListApiRequest
): { url: string; params: Record<string, any> } => {
  const { activeTab, page, search, appliedFilters } = request;

  // Build and validate filter parameters
  const filterParams = appliedFilters
    ? validateAndCleanFilters(appliedFilters)
    : {};

  const toAction = activeTab === InboxTabs.OPEN;
  return {
    url: "/employee-tickets/",
    params: {
      page,
      page_size: 10,
      to_action: toAction,
      ...(search && { search }),
      ...filterParams,
    },
  };
};

/**
 * Handles ticket resolution requests
 */
const resolveTicketRequest = async <T>(
  request: InboxQueryResolveApiRequest
): Promise<T> => {
  const { queryResolutionDataEnabled, resolve: ticketId } = request;

  if (!queryResolutionDataEnabled) {
    const { data } = await axios.get(
      `/employee-tickets/${ticketId}/mark_as_resolved/`
    );
    return data as T;
  }

  const params = {
    query_outcome: request.query_outcome,
    message_to_employee: request.message_to_employee,
    status: request.status,
  };

  if (request.status === InboxQueryStatus.CANCELLED) {
    const { data } = await axios.post(
      `/employee-tickets/${ticketId}/cancel/`,
      params
    );
    return data as T;
  }

  const { data } = await axios.post(
    `/employee-tickets/${ticketId}/resolve/`,
    params
  );
  return data as T;
};

/**
 * Handles inbox queries for listing, fetching details, or resolving tickets.
 *
 * @template T - Expected response type.
 * @param request - One of: InboxQueryListApiRequest, InboxQueryApiRequest, or InboxQueryResolveApiRequest.
 * @returns Promise<T> - The API response.
 *
 * - For list requests, fetches tickets by tab and page.
 * - For detail requests, fetches ticket by ID.
 * - For resolve requests, resolves or cancels a ticket.
 *
 * Feature flags:
 * - fetchingFromETSEnabled: Adds extra query param for list requests.
 * - queryResolutionDataEnabled: Uses POST for resolution, GET otherwise.
 */
const InboxQueryApiService = async <T>(
  request:
    | InboxQueryListApiRequest
    | InboxQueryApiRequest
    | InboxQueryResolveApiRequest
): Promise<T> => {
  if ("activeTab" in request && request.activeTab === InboxTabs.EXPORTS) {
    return Promise.resolve({} as T);
  }

  if ("id" in request) {
    const { data } = await axios.get(`/employee-tickets/${request.id}`);
    return data as T;
  }

  if ("activeTab" in request) {
    const { url, params } = buildListRequestConfig(request);
    const { data } = await axios.get(url, { params });
    return data as T;
  }

  if ("resolve" in request) {
    return resolveTicketRequest<T>(request);
  }

  throw new Error("Invalid request type");
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useInboxQueryListApiService = (
  request: InboxQueryListApiRequest
): UseQueryResult<InboxQueryListApiResponse, AxiosError> =>
  useQuery<InboxQueryListApiResponse, AxiosError>(
    [
      "InboxQueryListApiService",
      request.page,
      request.activeTab,
      request.search,
      request.fetchingFromETSEnabled,
      request.appliedFilters,
    ],
    () => InboxQueryApiService(request),
    { keepPreviousData: true }
  );

const useInboxQueryApiService = (
  request: InboxQueryApiRequest
): UseQueryResult<InboxQueryDetails | null, AxiosError> =>
  useQuery<InboxQueryDetails | null, AxiosError>(
    ["InboxQueryApiService", request.id],
    () => {
      if (!request.id) {
        return Promise.resolve(null);
      }
      return InboxQueryApiService<InboxQueryDetails>(request);
    },
    { keepPreviousData: true }
  );

const useInboxQueryResolveApiService = (): UseMutationResult<
  InboxQueryResolveApiResponse,
  AxiosError,
  InboxQueryResolveApiRequest
> => {
  const queryClient = useQueryClient();

  return useMutation<
    InboxQueryResolveApiResponse,
    AxiosError,
    InboxQueryResolveApiRequest
  >(
    (request: InboxQueryResolveApiRequest) => {
      const response =
        InboxQueryApiService<InboxQueryResolveApiResponse>(request);
      return response;
    },
    {
      onSuccess: () => {
        // Invalidate or refetch the relevant queries
        queryClient.invalidateQueries(["InboxQueryListApiService"]);
        queryClient.invalidateQueries(["InboxQueryApiService"]);
      },
      onError: () => {
        // A resolve can legitimately fail because the ticket has already advanced to the next
        // approver (backend returns 403 "not the next approver" — ENG-5210). Refetch the list and
        // detail so the UI reflects the ticket's current state and stops offering a stale
        // Approve/Decline control, rather than leaving the button live to fail again.
        queryClient.invalidateQueries(["InboxQueryListApiService"]);
        queryClient.invalidateQueries(["InboxQueryApiService"]);
      },
    }
  );
};

/**
 * Fetches filter options for inbox queries
 * Calls the real API endpoint to get current valid filter options
 */
const FilterOptionsApiService = async (): Promise<FilterOptionsResponse> => {
  const { data } = await axios.get("/employee-tickets/filter-types/");
  return data;
};

/**
 * React Query hook for fetching filter options
 */
const useFilterOptionsApiService = (): UseQueryResult<
  FilterOptionsResponse,
  AxiosError
> =>
  useQuery<FilterOptionsResponse, AxiosError>(
    ["FilterOptionsApiService"],
    FilterOptionsApiService,
    {
      staleTime: 2 * 60 * 1000, // 2 minutes - shorter time since data can change
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true, // Refetch when user returns to app
      retry: (failureCount, error) => {
        // Don't retry if it's a client error (4xx), but do retry for server errors
        if (
          error?.response?.status &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    }
  );

export {
  useInboxQueryListApiService,
  useInboxQueryApiService,
  useInboxQueryResolveApiService,
  useFilterOptionsApiService,
};
